/* Form-family park: the cell editor converts with the Table round (component-map.md). */
import {
  Button as ChakraButton,
  ButtonGroup as ChakraButtonGroup,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { Divider } from '@main/complykit/components/divider';
import { Switch } from '@main/complykit/components/switch';
import { Nullable } from '@main/shared/types';
import { FilterMode } from '@main/shared/url-helpers';
import { ColumnDef, Row, SortDirection } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

import { EditablePopover } from '../../editable/editable-popover';
import { BulkChangeConfigBase, useSyncIsBulkLoading } from '../bulk-actions';
import { createFilterHelper } from '../filters';
import { FilterLabelRow } from '../filters/filter-label-row';
import { FilterModeSelector } from '../filters/filter-mode';
import { CellContainer } from '../shared/cell';
import { CanEditGuard, getEditable, ifBulkEditable } from '../table';

type EditProps<TData> = {
  onChange: (row: Row<TData>, value: boolean) => void;
  canEditGuard?: CanEditGuard<TData, boolean | null | undefined>;
};

export type BulkBooleanOnChange<TData> = (rows: Row<TData>[], value: boolean) => unknown;
export type UseBulkBooleanChangeHook<TData> = (
  rows: Row<TData>[],
) => readonly [BulkBooleanOnChange<TData>, boolean];

type BooleanColumnConfig<TData, TFilter, TSort> = {
  id: string;
  header: string;
  edit?: EditProps<TData>;
  getColumnFilterCondition?(filterValue: boolean | null | undefined): Nullable<TFilter>;
  getColumnSort?(sortDirection: SortDirection): TSort;
  bulkEdit?: {
    useBulkChange: UseBulkBooleanChangeHook<TData>;
    bulkInitialValue?: boolean;
  } & Omit<BulkChangeConfigBase, 'onClick' | 'wrapper'>;
};

const filterHelper = createFilterHelper({
  filterSchema: z.boolean().optional().default(true),
  filterFn,
});

function filterFn(
  value: boolean | null | undefined,
  filterValue: boolean | null | undefined,
  filterMode?: FilterMode,
): boolean {
  if (typeof filterValue === 'undefined' || filterValue === null) {
    return true;
  }
  const targetState = !!filterValue;
  const actualState = !!value;
  return filterMode === FilterMode.Excludes
    ? actualState !== targetState
    : actualState === targetState;
}

export function boolean<TData, TFilter, TSort>() {
  return ({
    edit,
    bulkEdit,
    ...columnDef
  }: ColumnDef<TData, boolean | null | undefined> &
    BooleanColumnConfig<TData, TFilter, TSort>): ColumnDef<TData, boolean | null | undefined> => {
    const useBulkChange = bulkEdit?.useBulkChange;

    const editable = getEditable(edit);

    return {
      enableGlobalFilter: false,
      enableColumnFilter: false,
      enableSorting: false,
      sortDescFirst: false,

      ...columnDef,

      cell: (context) => {
        const value = context.getValue();
        const isEditable = edit && editable(context.row.original, context);

        return (
          <CellContainer cell={context.cell}>
            <Switch
              size="sm"
              isSelected={!!value}
              onChange={
                isEditable ? (isSelected) => edit?.onChange(context.row, isSelected) : undefined
              }
              isDisabled={!isEditable}
              aria-label={columnDef.header}
            />
          </CellContainer>
        );
      },

      filterFn: filterHelper.filterFn,

      meta: {
        ...columnDef.meta,
        columnType: 'boolean',
        editableStyles: false,
        editable,
        name: columnDef.header,
        // Tanstack table filters are untyped. Type safety is enforced via filter/column helpers
        getColumnFilterCondition: columnDef.getColumnFilterCondition as never,
        getColumnSort: columnDef.getColumnSort as never,

        bulkEdit: ifBulkEditable(
          edit,
          bulkEdit,
          () =>
            bulkEdit &&
            useBulkChange && {
              ...bulkEdit,
              id: columnDef.id ?? '',
              editor: (editorRows, trigger, isInMenu, onOpenChange, labelledBy) => (
                <BulkBooleanEditor
                  id={columnDef.id ?? ''}
                  key={columnDef.id}
                  trigger={trigger}
                  rows={editorRows}
                  useBulkChange={useBulkChange}
                  isInMenu={isInMenu}
                  onOpenChange={onOpenChange}
                  labelledBy={labelledBy}
                />
              ),
            },
        ),

        filter: {
          filterSchema: filterHelper.filterSchema,
          DisplayFilter: ({ column }) => {
            const { t } = useTranslation('ui');
            const mode = filterHelper.getFilterMode(column);
            const value = filterHelper.getFilterValue(column);
            return (
              <span>
                {mode === FilterMode.Excludes && t('table.filter.notLabel')}
                {typeof value === 'boolean'
                  ? value
                    ? t('table.column.boolean.enabled')
                    : t('table.column.boolean.disabled')
                  : null}
              </span>
            );
          },

          EditFilter: ({ column }) => {
            const { t } = useTranslation('ui');
            const value = filterHelper.getFilterValue(column);
            const modeOptions = useMemo(
              () => [
                { label: t('table.filter.is'), value: FilterMode.Includes },
                { label: t('table.filter.notLabel'), value: FilterMode.Excludes },
              ],
              [t],
            );

            return (
              <FormControl>
                <FormLabel fontWeight="medium" m={3}>
                  <FilterLabelRow label={columnDef.header}>
                    <FilterModeSelector {...filterHelper} column={column} options={modeOptions} />
                  </FilterLabelRow>
                </FormLabel>
                <Divider />

                {/* Parked with the Table round: the column's filter and edit
                    lists are engine chrome and convert with it. */}
                <ChakraButtonGroup
                  width="100%"
                  flexDirection="column"
                  overflow="hidden"
                  isAttached
                  size="sm"
                  variant="ghost"
                  py={2}
                  autoFocus
                >
                  <ChakraButton
                    onClick={(e) => {
                      filterHelper.setFilterValue(column, true);
                    }}
                    justifyContent="start"
                    fontWeight="normal"
                    borderRadius={0}
                    variant={value === true ? 'solid' : 'ghost'}
                  >
                    {t('table.column.boolean.enabled')}
                  </ChakraButton>
                  <ChakraButton
                    onClick={(e) => {
                      filterHelper.setFilterValue(column, false);
                    }}
                    fontWeight="normal"
                    justifyContent="start"
                    borderRadius={0}
                    variant={value === false ? 'solid' : 'ghost'}
                  >
                    {t('table.column.boolean.disabled')}
                  </ChakraButton>
                </ChakraButtonGroup>
              </FormControl>
            );
          },
        },
      },
    };
  };
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const anyValueChanged = <TData extends unknown>(
  rows: Row<TData>[],
  columnId: string,
  newValue: boolean | undefined,
): boolean =>
  rows.some((row) => {
    const value = row.getValue(columnId);
    const targetValue = !!newValue;
    const actualValue = !!value;
    return actualValue !== targetValue;
  });

type BulkBooleanEditorProps<TData> = {
  id: string;
  trigger: JSX.Element;
  rows: Row<TData>[];
  useBulkChange: UseBulkBooleanChangeHook<TData>;
  isInMenu?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  labelledBy?: string;
};

function BulkBooleanEditor<TData>({
  id,
  trigger,
  rows,
  useBulkChange,
  isInMenu,
  onOpenChange,
  labelledBy,
}: BulkBooleanEditorProps<TData>) {
  const { t } = useTranslation('ui');
  const [onChange, isLoading] = useBulkChange(rows);
  useSyncIsBulkLoading(id, isLoading);

  const onUpdate = async (value: boolean) => {
    if (anyValueChanged(rows, id ?? '', value)) {
      await onChange(rows, value);
    }
  };

  return (
    <EditablePopover
      onOpenChange={onOpenChange}
      openEvent={isInMenu ? 'mousedown' : undefined}
      placement={isInMenu ? 'left-start' : 'top-end'}
      closeOnBlur={false}
    >
      <EditablePopover.Trigger>{trigger}</EditablePopover.Trigger>
      <EditablePopover.Content
        py={2}
        bgColor="white"
        _dark={{ bgColor: 'gray.700' }}
        offset={-1}
        outline="2px solid"
        outlineColor="chakra-border-color"
        borderRadius="sm"
        minW="max-content"
      >
        <ChakraButtonGroup
          aria-labelledby={labelledBy}
          width="100%"
          flexDirection="column"
          overflow="hidden"
          isAttached
          size="sm"
          variant="ghost"
        >
          <ChakraButton
            onClick={(e) => {
              void onUpdate(true);
              (e.target as HTMLButtonElement).blur();
            }}
            justifyContent="start"
            fontWeight="normal"
            borderRadius={0}
          >
            {t('table.column.boolean.enabled')}
          </ChakraButton>
          <ChakraButton
            onClick={(e) => {
              void onUpdate(false);
              (e.target as HTMLButtonElement).blur();
            }}
            fontWeight="normal"
            justifyContent="start"
            borderRadius={0}
          >
            {t('table.column.boolean.disabled')}
          </ChakraButton>
        </ChakraButtonGroup>
      </EditablePopover.Content>
    </EditablePopover>
  );
}
